import React, { useRef, useCallback, useState } from 'react';
import { Modal, ThemeConfig } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { ModalFooter, useModal } from './Modal';
import { PaymentMethodFormBase } from './PaymentMethodForm';
import { ModalProps } from './Modal';
import { withTheme } from './WithTheme';
import { withAdapter } from '../utils/micro-fe';
import { PaymentMethodData, PaymentMethodFormRef, SavedCard } from '../types/payment';
import { usePaymentMethod } from '../hooks/usePaymentMethod';
import { StripeCardElementOptions } from '@stripe/stripe-js';
import { withStripeElements } from './withStripeElements';

export interface PaymentModalProps {
  formClassName?: string;
  inputClassName?: string;
  inputSuffixIconColor?: string;
  selectDropdownStyle?: React.CSSProperties;
  selectIconStyle?: React.CSSProperties;
  selectStyle?: React.CSSProperties;
  className?: string;
  headerClassName?: string;
  titleClassName?: string;
  modalProps?: ModalProps;
  isModalOpen?: boolean;
  entityId: string;
  advertiserId?: number;
  theme?: ThemeConfig;
  cardElementStyles?: {
    height?: string;
    padding?: string;
    fontSize?: string;
    backgroundColor?: string;
    borderColor?: string;
    focusBorderColor?: string;
    hoverBorderColor?: string;
    className?: string;
    containerClassName?: string;
    textColor?: string;
  };
  cardElementOptions?: StripeCardElementOptions;

  onSave?: (results: PaymentMethodData) => void;
  onError?: (error: any) => void;
  onClose?: () => void;
  onSaveCard?: (card: SavedCard) => void;
}

const PaymentMethodModal: React.FC<PaymentModalProps> = (props) => {
  const formRef = useRef<PaymentMethodFormRef>(null);
  const [isFormValid, setIsFormValid] = useState(false);

  // Use the payment method hook for consistent state management
  const {
    isProcessing,
    formData,
    savedCard,
    cardBrand,
    cardComplete,
    cardNumberComplete,
    cardExpiryComplete,
    cardCvcComplete,
    cardErrors,
    isLoading,
    handleSubmit,
    handleCardChange,
    clearCard,
    clearErrors,
    setFormField,
    fetchExistingPaymentMethod,
    elements,
    resetCardCompletionStates,
  } = usePaymentMethod({
    entityId: props.entityId,
    advertiserId: props.advertiserId,
    onSaveCard: props.onSaveCard,
  });

  const handleSave = useCallback(async () => {
    if (isProcessing) return; // Prevent multiple submissions

    try {
      const results = await formRef.current?.submit();

      // Check if the submission returned an error object
      if (results && typeof results === 'object' && 'error' in results) {
        // This is an error response, don't close the modal
        // The form will display the errors and stay open
        console.error('Payment method validation error:', results.message);
        props.onError?.(results);
        return;
      }

      // Success case - close modal and call onSave
      if (results && !('error' in results)) {
        props.onSave?.(results as PaymentMethodData);
      }
    } catch (error) {
      console.error('Error in payment method save:', error);
      props.onError?.(error);
    }
  }, [isProcessing, props.onSave, props.onError]);

  const handleCloseModal = useCallback(() => {
    // Reset form state when modal is closed
    formRef.current?.resetForm();
    clearErrors();
    props.onClose?.();
  }, [props.onClose]);

  const handleFormValidation = useCallback((isValid: boolean) => {
    setIsFormValid(isValid);
  }, []);

  const component = (
    <Modal
      open={props.isModalOpen}
      onCancel={handleCloseModal}
      footer={
        <ModalFooter
          onSave={handleSave}
          handleCloseModal={handleCloseModal}
          disabled={isProcessing || !isFormValid}
          modalFooterClassName={props.modalProps?.footerClassName}
          modalButtonClassName={props.modalProps?.buttonClassName}
          modalCancelButtonClassName={props.modalProps?.cancelButtonClassName}
        />
      }
      className={`billing-ui ${props.modalProps?.className}`}
      width={600}
      styles={{
        content: {
          padding: '0px',
          borderRadius: '4px',
          ...props.modalProps?.style
        },
        footer: { marginTop: '0px' }
      }}
      closeIcon={
        <CloseOutlined
          className={`pt-2 pr-2 ${props.modalProps?.closeButtonClassName}`}
        />
      }
    >
      <div className={`flex flex-col rounded-lg ${props.modalProps?.className}`}>
        <div className={`pt-5 pb-5 bg-white rounded-lg border-b border-gray-200 ${props.modalProps?.headerClassName}`}>
          <div className={`text-left px-4 flex justify-between ${props.modalProps?.titleClassName}`}>
            <span>Payment Method</span>
          </div>
        </div>
        <div className={`px-[30px] mt-[16px] overflow-y-auto flex-1 max-h-[500px] ${props.modalProps?.contentContainerClassName}`}>
          <PaymentMethodFormBase
            formClassName={props.formClassName}
            inputClassName={props.inputClassName}
            inputSuffixIconColor={props.inputSuffixIconColor}
            selectDropdownStyle={props.selectDropdownStyle}
            selectIconStyle={props.selectIconStyle}
            ref={formRef}
            cardElementStyles={props.cardElementStyles}
            cardElementOptions={props.cardElementOptions}
            formData={formData}
            savedCard={savedCard}
            cardBrand={cardBrand}
            cardComplete={cardComplete}
            cardNumberComplete={cardNumberComplete}
            cardExpiryComplete={cardExpiryComplete}
            cardCvcComplete={cardCvcComplete}
            cardErrors={cardErrors}
            isLoading={isLoading}
            isProcessing={isProcessing}
            handleSubmit={handleSubmit}
            handleCardChange={handleCardChange}
            clearCard={clearCard}
            clearErrors={clearErrors}
            setFormField={setFormField}
            fetchExistingPaymentMethod={fetchExistingPaymentMethod}
            elements={elements}
            onFormValidation={handleFormValidation}
            resetCardCompletionStates={resetCardCompletionStates}
          />
        </div>
      </div>
    </Modal>
  );

  return props.theme ? withTheme({ theme: props.theme, children: component }) : component;
};

const WrappedPaymentMethodModal = withAdapter<PaymentModalProps>(withStripeElements(PaymentMethodModal));

export default WrappedPaymentMethodModal; 
